// Regras puras da tela de Contatos (mockup "Tela Aeros Dark - Contatos e
// Modais"): avatar, número formatado, tags, paginação, exportação e
// mapeamento da importação de planilha.
import { AEROS } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";

export const initialsOf = (name = "") =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

export const avatarColorFor = (id) =>
  AEROS.avatarCycle[Math.abs(Number(id) || 0) % AEROS.avatarCycle.length];

// "+55 11 98432-1187" a partir de "5511984321187"; outros países só com
// espaço após o DDI; grupos e valores sem dígito voltam como vieram.
export const formatPhone = (number) => {
  const raw = String(number || "");
  const digits = raw.replace(/\D/g, "");
  if (!digits || raw.includes("@g.us")) return raw;
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) {
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    const head = rest.slice(0, rest.length - 4);
    const tail = rest.slice(-4);
    return `+55 ${ddd} ${head}-${tail}`;
  }
  if (digits.length > 10) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
  return raw;
};

// Máscara LGPD para atendente comum: mantém o começo e os 2 últimos dígitos.
export const maskPhone = (formatted, number) => {
  const base = formatted || String(number || "");
  const tail = String(number || "").slice(-2);
  return `${base.slice(0, -6)}**-**${tail}`;
};

export const walletNameOf = (contact) => {
  const w = contact?.contactWallets?.[0]?.wallet;
  return w?.name || "";
};

// ---- paginação numerada (rodapé "Mostrando X de Y ‹ 1 2 ›") ----
export const totalPages = (count, pageSize) =>
  Math.max(1, Math.ceil((Number(count) || 0) / pageSize));

// Janela de até `size` páginas centrada na atual, presa às bordas.
export const pageWindow = (current, total, size = 5) => {
  const span = Math.min(size, total);
  let start = Math.max(1, current - Math.floor(span / 2));
  start = Math.min(start, total - span + 1);
  return Array.from({ length: span }, (_, i) => start + i);
};

// Chip da coluna Tags: cor da tag no texto, fundo com 14% de alpha.
const hexToRgb = (hex) => {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return Number.isNaN(n) || full.length !== 6 ? null : [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

export const tagChip = (tag) => {
  const rgb = hexToRgb(tag?.color);
  const color = rgb ? tag.color : "#9CA3B8";
  const [r, g, b] = rgb || [156, 163, 184];
  return { label: tag?.name || "", color, bg: `rgba(${r},${g},${b},0.14)` };
};

// Linha da planilha exportada (mesmo contrato do modelo de importação).
export const exportRow = (contact, { masked = false } = {}) => ({
  name: contact.name,
  number: contact.isGroup || !masked ? contact.number : maskPhone(null, contact.number),
  email: contact.email || "",
  birthDate: contact.birthDate || "",
  tags: (contact.tags || []).map((t) => t.name).join(", "),
  carteira: contact.contactWallets?.[0]?.wallet?.email || "",
});

export const EXPORT_MODEL_ROW = {
  name: "Nome Contato",
  number: "5599999999999",
  email: "email-contato@email.com",
  birthDate: "15/05/1990",
  tags: "tag1, tag2",
  carteira: "funcionario-empresa@email.com",
};

// ---- importação de planilha ----
const fld = (id, key, required) => ({ id, required, get label() { return i18n.t(`aeros.contacts.${key}`); } });
export const IMPORT_FIELDS = [
  fld("name", "colName", true),
  fld("number", "colWhatsapp", true),
  fld("email", "colEmail", false),
  fld("birthDate", "colBirthDate", false),
  fld("tags", "colTags", false),
  fld("carteira", "colWallet", false),
];

const FIELD_ALIASES = {
  name: ["nome", "name", "nome completo", "nome_completo", "contato"],
  number: ["numero", "telefone", "celular", "whatsapp", "fone", "number", "phone"],
  email: ["email", "e-mail", "mail"],
  birthDate: ["data de nascimento", "nascimento", "data nascimento", "data_nascimento", "dt nasc", "birthdate"],
  tags: ["tags", "tag", "etiquetas"],
  carteira: ["carteira", "wallet"],
};

const normalizeHeader = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

// Pré-mapeia colunas pelo cabeçalho: { [colIndex]: fieldId }
export const guessMapping = (headerRow = []) => {
  const mapping = {};
  const used = new Set();
  headerRow.forEach((cell, index) => {
    const header = normalizeHeader(cell);
    if (!header) return;
    const field = Object.keys(FIELD_ALIASES).find(
      (id) => !used.has(id) && FIELD_ALIASES[id].includes(header)
    );
    if (!field) return;
    mapping[index] = field;
    used.add(field);
  });
  return mapping;
};

const excelSerialToDate = (serial) => {
  const ms = (serial - 25569) * 86400000;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toYMD = (d, utc = false) => {
  const y = utc ? d.getUTCFullYear() : d.getFullYear();
  const m = (utc ? d.getUTCMonth() : d.getMonth()) + 1;
  const day = utc ? d.getUTCDate() : d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const parseDateCell = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let d = null;
  let utc = false;
  // sheet_to_json com raw:false entrega o serial como string ("33029")
  const serial = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
  if (typeof serial === "number" && serial > 1 && serial < 100000) {
    d = excelSerialToDate(serial);
    utc = true; // serial do Excel = meia-noite UTC
  } else if (typeof value === "string") {
    const parts = value.split("/");
    if (parts.length === 3) {
      const [a, b, c] = parts.map((p) => parseInt(p, 10));
      // dd/mm/aaaa quando o primeiro passa de 12; senão assume mm/dd (Excel)
      d = a > 12 ? new Date(c, b - 1, a) : new Date(c, a - 1, b);
    } else {
      d = new Date(value);
    }
  } else if (value instanceof Date) {
    d = value;
  }
  if (!d || Number.isNaN(d.getTime()) || d > new Date()) return null;
  return toYMD(d, utc);
};

// Prévia da planilha: serial/mm-dd viram dd/mm/aaaa; o resto volta como veio.
export const displayDateCell = (value) => {
  const ymd = parseDateCell(value);
  if (!ymd) return value;
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
};

export const normalizeNumber = (value) => {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits && !digits.startsWith("55") && digits.length <= 11) digits = `55${digits}`;
  return digits;
};

// Converte uma linha da planilha em contato; null quando falta nome/número.
// Campo "Info extra: <cabeçalho>" no mapeamento vira campo personalizado.
export const EXTRA_PREFIX = "extra:";
export const extraField = (label) => `${EXTRA_PREFIX}${String(label || "").trim()}`;
export const isExtraField = (field) => String(field || "").startsWith(EXTRA_PREFIX);

export const rowToContact = (row, mapping, { extraTag } = {}) => {
  const contact = {};
  Object.entries(mapping).forEach(([index, field]) => {
    const value = row[Number(index)];
    if (value === undefined || value === null || value === "") return;
    if (field === "number") contact.number = normalizeNumber(value);
    else if (field === "birthDate") {
      const parsed = parseDateCell(value);
      if (parsed) contact.birthDate = parsed;
    } else if (isExtraField(field)) {
      const name = field.slice(EXTRA_PREFIX.length);
      if (!name) return;
      contact.extraInfo = [...(contact.extraInfo || []), { name, value: String(value).trim() }];
    } else contact[field] = String(value).trim();
  });
  if (!contact.name || !contact.number || contact.number.length < 10) return null;
  if (extraTag) {
    contact.tags = contact.tags ? `${contact.tags}, ${extraTag}` : extraTag;
  }
  return contact;
};

// Separa linhas válidas e inválidas (número da linha na planilha, 1-based,
// contando o cabeçalho) para o aviso "N linhas com número inválido serão
// ignoradas". `selected` (índice da linha → true) restringe o lote.
export const buildImportBatch = (rows, mapping, { extraTag, selected } = {}) => {
  const contatos = [];
  const invalid = [];
  const nameCol = Object.keys(mapping || {}).find((k) => mapping[k] === "name");
  for (let i = 1; i < (rows || []).length; i++) {
    const row = rows[i];
    if (selected && !selected[i]) continue;
    if (!row || row.every((c) => c === "" || c === null || c === undefined)) continue;
    const contact = rowToContact(row, mapping, { extraTag });
    if (contact) contatos.push(contact);
    else invalid.push({ line: i + 1, name: nameCol === undefined ? "" : String(row[nameCol] ?? "") });
  }
  return { contatos, invalid };
};

// Campos obrigatórios ainda sem coluna mapeada.
export const mappingIssues = (mapping) => {
  const fields = Object.values(mapping || {});
  return IMPORT_FIELDS.filter((f) => f.required && !fields.includes(f.id)).map((f) => f.id);
};

// Até este tamanho o modal acompanha a importação ao vivo até o fim; acima
// disso ela roda em segundo plano e o usuário recebe a notificação global.
export const LIVE_IMPORT_LIMIT = 500;
export const importMode = (count) => (Number(count) > LIVE_IMPORT_LIMIT ? "background" : "live");
